import * as _ from "lodash";
import { IdsAttribute, IdsFilterSpec, IdsMetric, IdsNode, IdsRow, IMetricDataSetProvider } from "./PA.MetricDataSet.IF";
import { IJSColumnCalc, IMetricDataProviderOptions } from "./IMetricDataProviderOptions";
import { ISearchRequest, PASearch } from "../PA.Core/PASearch";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ExceptionMessage_restate, WarningMessage_restate } from "../PA.Core/Basic/MessageUtils";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { ISQLGroupByCalc } from "../../server-pa/PA.Core/PA.Core.IF";
import { FilterCondition, FilterUtils } from "../PA.Core/Basic/Filter";
import { MathNode, parse, SymbolNode } from "mathjs";
import { IMessageProvider } from "../PA.Core/PA.Core.IF.Basic";
import { ExpressionUtils } from "../PA.Core/Basic/ExpressionUtils";

interface IdbAttribute extends IdsAttribute {
    // Column Metadata needed for this Data Provider
    columnName: string;
    //fkMapperId: string;
    //fkIdField: string;
    //fkIdUIField: string;
    //fkNameField: string;
    fks: {
        entityId: string;
        bUseDbContext: boolean;
        idField: string;
        idUIField: string;
        nameField: string;
        orderField?: string;
        orderDescending?: boolean;
        condition?: FilterCondition;
    }[]
}

interface IdbMetric
    extends IdsMetric {
    columnName: string;
    columnSQLFunction?: "SUM" | "AVG" | "MIN" | "MAX" | "MEDIAN" | "COUNT" | "STDDEV";
    columnSQLArg?: string;
}

interface IdbMetricOutputCalc
    extends IdsMetric,
        IJSColumnCalc {

}

interface IFilterGroups {
    attributeOnly: IdsFilterSpec[],
    metricOnly: IdsFilterSpec[],
    mix: IdsFilterSpec[],
    unknown: IdsFilterSpec[]
}


export class DBMetricDataSetProvider implements IMetricDataSetProvider {
    private dbContext: PAdbContext;
    private _createMessages: MessageProvider;
    private _createOptions: IMetricDataProviderOptions;
    private _name: string;

    private _bLoaded: boolean;
    private _onLoadComplete: Promise<void>;
    private _searchSchema: { [fld: string]: { type: string } };
    private _dctAttribute: { [attributeId: string]: IdbAttribute };
    private _dctMetricColumn: { [metricId: string]: IdbMetric };
    private _dctMetricOutputCalc: { [metricId: string]: IdbMetricOutputCalc };
    private dctColumnName: { [symbolId: string]: string };
    private dctSimpleType: { [symbolId: string]: "number" | "string" | "date" };

    private dctAttrNodes: { [attributeId: string]: { [wbId: number]: IdsNode } };

    constructor(createOptions?: IMetricDataProviderOptions) {
        this._createOptions = createOptions;
        this._createMessages = new MessageProvider();
        this._bLoaded = false;
        this._searchSchema = {};
        this._dctAttribute = {};
        this._dctMetricColumn = {};
        this._dctMetricOutputCalc = {};
        this.dctAttrNodes = {};
        this.dctSimpleType = {};
        this._name = createOptions?.name || `Database Metric Provider ${createOptions?.objectName}`;
        this.dbContext = createOptions?.dbContext;
        if (_.isEmpty(this.dbContext)) this.dbContext = new PAdbContext();
    }

    get CreateOptions(): IMetricDataProviderOptions {
        return this._createOptions;
    }

    get bLoaded(): boolean {
        return this._bLoaded;
    }

    get CreateMessages(): IMessageProvider {
        return this._createMessages;
    }

    OnLoadComplete(): Promise<void> {
        if (this._onLoadComplete) return this._onLoadComplete;

        const rv = (this._onLoadComplete = (async () => {
            const mp = this._createMessages;
            //--
            try {
                const strColumnNames: string[] = [];
                const nmbColumnNames: string[] = [];
                _.forEach(this._createOptions.attributes, (attr) => {
                    switch (attr.type) {
                        case "number":
                        case "sapdtts":
                        case "node":
                            if (!_.isEmpty(attr.columnName)) nmbColumnNames.push(attr.columnName);
                            this.dctSimpleType[attr.id] = "number";
                            break;
                        case "sapdate":
                        case "string":
                            if (!_.isEmpty(attr.columnName)) strColumnNames.push(attr.columnName);
                            this.dctSimpleType[attr.id] = "string";
                            break;
                        case "date":
                            this.dctSimpleType[attr.id] = "date";
                            break;
                        default:
                            // report error, unrecognized type
                            // Add warning
                            WarningMessage_restate(
                                mp,
                                true,
                                "UnknownType",
                                `Unknown Type ${attr.type} for attribute Id ${attr.id}`
                            );
                            return; // next Attribute
                    }

                    // All Attributes should have a column
                    if (_.isEmpty(attr.columnName)) {
                        WarningMessage_restate(
                            mp,
                            true,
                            "AttrColumnUnknown",
                            `Attribute Id ${attr.id} has no column specification`
                        );
                        return; // next Attribute
                    }

                    const dbAttribute: IdbAttribute = {
                        Id: attr.id,
                        Name: attr.name,
                        ShortName: attr.shortName || attr.name,
                        Type: attr.type,
                        SortOrder: attr.sort || 99999,
                        columnName: attr.columnName,

                        // Only for Node attributes
                        fks: []
                    };

                    if (!_.isEmpty(attr.fkMapperId)) {
                        dbAttribute.fks.push({
                            entityId: attr.fkMapperId,
                            bUseDbContext: true,
                            idField: attr.fkIdField,
                            idUIField: attr.fkIdUIField,
                            nameField: attr.fkNameField,
                            orderField: attr.fkOrderField,
                            orderDescending: attr.fkOrderDescending,
                            condition: undefined
                        });
                    }
                    if (!_.isEmpty(attr.fks)) {
                        _.forEach(attr.fks, (fk) => {
                            dbAttribute.fks.push({
                                entityId: fk.entityId,
                                bUseDbContext: true,
                                idField: fk.idField,
                                idUIField: fk.idUIField,
                                nameField: fk.nameField,
                                orderField: fk.orderField,
                                orderDescending: fk.orderDescending,
                                condition: fk.condition
                            });
                        });
                    }

                    if (dbAttribute.Type == "node") {
                        // Add static nodes
                        if (!(attr.id in this.dctAttrNodes)) this.dctAttrNodes[attr.id] = {};
                        const nodeCache = this.dctAttrNodes[attr.id];
                        _.forEach(attr.staticNodes || [], (aNode) => {
                            const node: IdsNode & any = {
                                id: aNode.id,
                                id_ui: aNode.id_ui || ("" + aNode.id),
                                name: aNode.name || aNode.id_ui || ("" + aNode.id)
                            };

                            if (!_.isUndefined(aNode.nodeOrder))
                                node.nodeOrder = aNode.nodeOrder;

                            nodeCache[node.id] = node;
                        });

                        // Check the mapper/ view id
                        _.forEach(dbAttribute.fks, (fk) => {
                            const mapper = this.dbContext.getMapper(fk.entityId);
                            if (_.isEmpty(mapper)) {
                                fk.bUseDbContext = false
                                if (_.isEmpty(fk.idField)) {
                                    WarningMessage_restate(
                                        mp,
                                        true,
                                        "AttrUnknownMapper",
                                        `Attribute ${attr.id} unknown mapper ${fk.entityId} or id field missing`
                                    );
                                    return;
                                }
                            } else {

                                if (_.isEmpty(fk.idField))
                                    fk.idField = mapper.idAttribute;

                                // Could also check schema to see if the fields are in the mapper
                                const dctProps = mapper.schema?.properties || {};
                            }
                        })
                    }

                    if (!_.isEmpty(attr.numberFormat))
                        (<any>dbAttribute).numberFormat = attr.numberFormat;

                    if (!_.isEmpty(attr.dateFormat))
                        (<any>dbAttribute).dateFormat = attr.dateFormat;

                    this._dctAttribute[attr.id] = dbAttribute;
                });


                _.forEach(this._createOptions.metrics, (mtrc) => {

                    // All Metric should have a column or calculation
                    if (_.isEmpty(mtrc.columnName) && _.isEmpty(mtrc.outputCalc?.formula)) {
                        WarningMessage_restate(
                            mp,
                            true,
                            "MtrcColumnUnknown",
                            `Metric Id ${mtrc.id} has no column or calc specification`
                        );
                        return; // next Metric
                    }

                    this.dctSimpleType[mtrc.id] = "number";

                    if (!_.isEmpty(mtrc.columnName)) {
                        const dbMetric: IdbMetric = {
                            Id: mtrc.id,
                            Name: mtrc.name,
                            ShortName: mtrc.shortName || mtrc.name,
                            format: mtrc.format,
                            SortOrder: mtrc.sort || 9999,
                            Hidden: !!mtrc.hidden,
                            currency: !!mtrc.currency,
                            rowAggType: mtrc.rowAggType,
                            rowAggWeightMetricId: mtrc.rowAggWeightMetricId,
                            columnName: mtrc.columnName
                        };
                        if (!_.isEmpty(mtrc.columnSQLArg))
                            dbMetric.columnSQLArg = mtrc.columnSQLArg;
                        if (!_.isEmpty(mtrc.columnSQLFunction))
                            dbMetric.columnSQLFunction = mtrc.columnSQLFunction;
                        this._dctMetricColumn[mtrc.id] = dbMetric;
                    } else {
                        // calculated Metric
                        const calcMetric: IdbMetricOutputCalc = {
                            Id: mtrc.id,
                            Name: mtrc.name,
                            ShortName: mtrc.shortName || mtrc.name,
                            format: mtrc.format,
                            SortOrder: mtrc.sort || 9999,
                            Hidden: !!mtrc.hidden,
                            currency: !!mtrc.currency,
                            rowAggType: mtrc.rowAggType,
                            rowAggWeightMetricId: mtrc.rowAggWeightMetricId,
                            aggregateExpression: mtrc.outputCalc?.formula,
                            formula: mtrc.outputCalc?.formula,
                            requiredMetricsIds: mtrc.outputCalc?.requiredMetricsIds || [],
                            storeKey: mtrc.outputCalc?.storeKey || `dsCalc_${mtrc.id}`
                        };
                        if (_.isEmpty(calcMetric.storeKey))
                            calcMetric.storeKey = `dsCalc_${mtrc.id}`;
                        const dctSymbols: { [id: string]: string } = {};
                        const parsedFormula = parse(calcMetric.formula);
                        parsedFormula.traverse((node: MathNode & SymbolNode, path, parent) => {
                            if (node.type == "SymbolNode")
                                dctSymbols[node.name] = node.name;
                        });
                        _.forEach(_.values(dctSymbols), (aSymbolName) => {
                            if (_.includes(calcMetric.requiredMetricsIds, aSymbolName))
                                return;
                            calcMetric.requiredMetricsIds.push(aSymbolName);
                        });
                        calcMetric.evalFunc = parsedFormula.compile();

                        this._dctMetricOutputCalc[mtrc.id] = calcMetric;
                    }


                });
                this._searchSchema = PASearch.createSimpleSchema(strColumnNames, nmbColumnNames);

                // Create Dictionary of Column
                this.dctColumnName = {};
                _.forEach(this._dctAttribute, (attr) => (this.dctColumnName[attr.Id] = attr.columnName));
                _.forEach(this._dctMetricColumn, (mtrc) => (this.dctColumnName[mtrc.Id] = mtrc.columnName));

                this._bLoaded = true;
            } catch (ex) {
                ExceptionMessage_restate(mp, ex);
            }
        })());

        return rv;
    }

    get Name(): string {
        return this._name;
    }

    getAttributes(): IdsAttribute[] {
        return _.values(this._dctAttribute);
    }

    getMetrics(): IdsMetric[] {
        return _.union(<IdsMetric[]>_.values(this._dctMetricColumn),
            <IdsMetric[]>_.values(this._dctMetricOutputCalc));
    }

    getRowAttribute(row: IdsRow, attributeId: string): any {
        const colName = this.dctColumnName[attributeId];
        return (<any>row || {})[colName];
    }

    getRowMetric(row: IdsRow, metricId: string): number {
        if (metricId in this._dctMetricColumn) {
            const colName = this.dctColumnName[metricId];
            return (<any>row || {})[colName];
        }
        if (metricId in this._dctMetricOutputCalc) {
            const oc = this._dctMetricOutputCalc[metricId];
            return this.getRowData(row, oc.storeKey);
        }
    }

    getRowData(row: IdsRow, keyId: string): any {
        return (<any>(row || {}))?.keyStore?.[keyId];
    }

    setRowData(row: IdsRow, keyId: string, value: any): void {
        if (_.isEmpty(row)) return undefined;
        const anyRow = <any>row;
        anyRow.keyStore = anyRow.keyStore || {};
        anyRow.keyStore[keyId] = value;
    }

    getRowAggregateMetric(rows: IdsRow[], metricId: string): number {
        const self = this;
        const mtr = self._dctMetricOutputCalc[metricId];
        if (_.isEmpty(mtr?.aggregateExpression))
            return undefined;

        const aggExpr = mtr.aggregateExpression;

        function getSymbol(row: IdsRow, symbolId: string): number {
            return self.getRowMetric(row, symbolId);
        }

        const aggMtrValue = ExpressionUtils.getAggregate(aggExpr, rows, getSymbol);
        return aggMtrValue;
    }


    async getAttributeNodes(attributeId: string, ids: number[]): Promise<IdsNode[]> {
        const attr = this._dctAttribute[attributeId];
        if (attr?.Type != "node") return [];

        const dct: { [id: number]: IdsNode } = {};
        if (!(attributeId in this.dctAttrNodes)) this.dctAttrNodes[attributeId] = {};

        const nodeCache = this.dctAttrNodes[attributeId];
        let nodeIdsToFind: number[] = [];
        _.forEach(ids, (anId) => {
            if (anId in dct) return;
            if (anId in nodeCache) {
                dct[anId] = nodeCache[anId];
                return;
            }
            nodeIdsToFind.push(anId);
        });

        // TODO: For Unknown Keys, should we auto back fill empty nodes ?
        // remove nodes with Id = 0
        _.remove(nodeIdsToFind, (id) => (id == 0) || _.isUndefined(id) || _.isNull(id));

        for (let fk of attr.fks) {
            if (_.isEmpty(nodeIdsToFind))
                break;

            // Use the dbContext, Attribute Metadata to retrieve nodes
            let recs: { [id: string]: any }[] = [];
            if (fk.bUseDbContext) {
                recs = await this.dbContext.findUsingCache(fk.entityId, nodeIdsToFind);
            } else {
                let searchCnd: FilterCondition = { [fk.idField]: { inq: nodeIdsToFind } }
                if (!_.isEmpty(fk.condition))
                    searchCnd = { and: [searchCnd, fk.condition] }

                recs = await PASearch.find(fk.entityId, searchCnd,
                    { [fk.idField]: { type: "number" } }, [], 0, []);
            }
            _.forEach(recs, (aRow: any) => {
                const id = aRow[fk.idField];
                const id_ui = aRow[fk.idUIField];
                const name = aRow[fk.nameField];

                const node: IdsNode = {
                    id: id,
                    id_ui: id_ui || ("" + id),
                    name: name || id_ui || ("" + id)
                };

                if (!_.isEmpty(fk.orderField)) {
                    const orderVal = Number(aRow[fk.orderField]);
                    if (!_.isNaN(orderVal))
                        (<any>node).nodeOrder = fk.orderDescending ? -orderVal : orderVal;
                }
                if (!(node.id in nodeCache))
                    nodeCache[node.id] = node;

                if (!(node.id in dct))
                    dct[node.id] = node;
            });
            // filter found keys
            nodeIdsToFind = _.filter(nodeIdsToFind, (id) => !(id in dct))
        }

        return _.values(dct);
    }

    private getGroupFilters(filters: IdsFilterSpec[]): IFilterGroups {
        const rv: IFilterGroups = {
            attributeOnly: [],
            metricOnly: [],
            mix: [],
            unknown: []
        };

        _.forEach(filters, (flt) => {
            let bHasAttributeRef = false;
            let bHasMetricRef = false;
            let bHasUnknownRef = false;

            const symbols = FilterUtils.getSymbolReferences(flt.condition);

            _.forEach(symbols, (s) => {
                if (s in this._dctAttribute)
                    bHasAttributeRef = true;
                else if (s in this._dctMetricColumn) {
                    bHasMetricRef = true;
                } else if (s in this._dctMetricOutputCalc) {
                    bHasMetricRef = true;
                } else {
                    bHasUnknownRef = true;
                }
            });

            if (bHasUnknownRef)
                rv.unknown.push(flt);
            else if (bHasAttributeRef && bHasMetricRef)
                rv.mix.push(flt);
            else if (bHasMetricRef)
                rv.metricOnly.push(flt);
            else if (bHasAttributeRef)
                rv.attributeOnly.push(flt);
        });

        return rv;

    }

    private Rows_filter(inputRows: IdsRow[], filters: IdsFilterSpec[]): IdsRow[] {
        if (_.isEmpty(filters)) return inputRows;

        const self = this;

        // Check Functions from Conditions
        function symbolGet(symbolId: string, row: IdsRow): any {
            if (symbolId in self._dctAttribute) return self.getRowAttribute(row, symbolId);

            if (symbolId in self._dctMetricColumn) return self.getRowMetric(row, symbolId);

            if (symbolId in self._dctMetricOutputCalc) return self.getRowMetric(row, symbolId);

        }

        const cndFunctions = _.map(filters, (flt) => {
            return FilterUtils.createFunction(flt.condition, self.dctSimpleType, symbolGet);
        });

        // Check Functions
        const notFunctions = _.remove(cndFunctions, (a) => !_.isFunction(a));
        // Anything we can't make a function , simply skip for now

        const filterRows: IdsRow[] = [];
        _.forEach(inputRows, (row) => {
            let bInclude = true;
            _.forEach(cndFunctions, (fnc) => {
                const bRes = fnc(row);
                if (!bRes) {
                    bInclude = false;
                    return true; // stop iterating
                }
            });

            if (bInclude) filterRows.push(row);
        });

        return filterRows;
    }


    private createSearchCondition(filters: IdsFilterSpec[]): { [id: string]: any } {
        const cnds: { [id: string]: any }[] = [];

        _.forEach(filters, (flt) => {
            if (_.isEmpty(flt) || _.isEmpty(flt?.condition)) return;

            const newCnd = FilterUtils.changeSymbols(flt.condition, this.dctColumnName);
            if (_.isEmpty(newCnd)) {
                const symbols = FilterUtils.getSymbolReferences(flt.condition);
                console.log(
                    `Unable to convert Condition ${JSON.stringify(flt?.condition)} with Symbols ${symbols.join(", ")}`
                );
            } else {
                cnds.push(newCnd);
            }
        });

        if (!_.isEmpty(this._createOptions.staticDatabaseFilters))
            _.forEach(this._createOptions.staticDatabaseFilters, (sf) => {
                cnds.push(sf);
            })

        if (_.isEmpty(cnds)) return {};

        return { and: cnds };
    }


    async getDataSetRowCount(
        arrAttributeIds: string[],
        filters: IdsFilterSpec[]
    ): Promise<number> {
        const self = this;

        const results = await self.getDataRows(
            arrAttributeIds,
            [],
            filters,
            0,
            true
        );
        if (results.length == 1 && "CNT" in results[0]) {
            // @ts-expect-error "Typing"
            return results["CNT"];
        }
        return results.length;
    }


    async getDataRows(
        arrAttributeIds: string[],
        arrMetricIds: string[],
        filters: IdsFilterSpec[],
        rowCountThreshold?: number,
        bCountOnly?: boolean
    ): Promise<IdsRow[]> {
        const self = this;

        const grpFlt = this.getGroupFilters(filters);

        const cnd = this.createSearchCondition(grpFlt.attributeOnly);
        let limit = this._createOptions?.rowCountThreshold || 0;
        if (_.isNumber(rowCountThreshold)) limit = rowCountThreshold;

        const arrNumberSQLColumns: string[] = [];

        const attrColNames: string[] = [];
        _.forEach(arrAttributeIds, (attrId) => {
            const colName = this.dctColumnName[attrId];
            const attr = this._dctAttribute[attrId];
            if (!_.isEmpty(colName)) {
                attrColNames.push(colName);
                if (attr?.Type == "number") arrNumberSQLColumns.push(colName);
            }
        });

        // Determine what metrics we need to load based
        // 1. Check for dependent metrics


        const dctMtrcSQLgroupByCalcs: { [metricId: string]: ISQLGroupByCalc } = {};

        //
        // Only push to CalcOrder once your dependencies have been pushed
        const outputCalcOrder: string[] = [];
        const dctCalcOrder: { [metricId: string]: string } = {};

        let recursiveErrorCount = 0;
        if (!bCountOnly) {
            _.forEach(arrMetricIds, (topMetricId) => {

                function addMetric(mtrcId: string, encountered: { [metricId: string]: string }) {
                    if (mtrcId in encountered) {
                        if (!(mtrcId in dctCalcOrder) && (mtrcId in self._dctMetricOutputCalc)) {
                            recursiveErrorCount++;
                        }
                        return;
                    }
                    encountered[mtrcId] = mtrcId;

                    // Get the metric from SQL,
                    //-- Column metrics have no dependents
                    if (mtrcId in self._dctMetricColumn) {
                        const colMetric = self._dctMetricColumn[mtrcId];
                        if (!(mtrcId in dctMtrcSQLgroupByCalcs)) {
                            const grpByCalc: ISQLGroupByCalc = {
                                srcCalc: colMetric.columnName,
                                outFieldName: colMetric.columnName
                            };
                            if (!_.isEmpty(colMetric.columnSQLArg))
                                grpByCalc.srcCalc = colMetric.columnSQLArg;
                            if (!_.isEmpty(colMetric.columnSQLFunction))
                                grpByCalc.func = colMetric.columnSQLFunction;

                            dctMtrcSQLgroupByCalcs[mtrcId] = grpByCalc;
                            arrNumberSQLColumns.push(colMetric.columnName);
                        }
                    } else if (mtrcId in self._dctMetricOutputCalc) {
                        // 1. Add Child Metrics first
                        const ocMetric = self._dctMetricOutputCalc[mtrcId];
                        for (const childMetricId of ocMetric.requiredMetricsIds) {
                            addMetric(childMetricId, encountered);
                        }
                        // 2. Add self to calc order
                        if (!(mtrcId in dctCalcOrder)) {
                            dctCalcOrder[mtrcId] = mtrcId;
                            outputCalcOrder.push(mtrcId);
                        }

                    }
                }

                addMetric(topMetricId, {});
            });

            if (_.isEmpty(dctMtrcSQLgroupByCalcs)) {
                // create dummy
                dctMtrcSQLgroupByCalcs["XK"] = {
                    srcCalc: "1",
                    outFieldName: "XK"
                };
            }
        }

        const bIsRawProcCall = this._createOptions.objectName.indexOf("CALL ") > -1;

        let request: ISearchRequest = {}
        if (bIsRawProcCall) {
            request = {
                sqlQuery: this._createOptions.objectName,
                bRunProc: true
            }
        } else {
            request = {
                model: this._createOptions.objectName,
                fields: this._searchSchema,
                limit: limit,
                where: cnd,
                distinct: attrColNames,
                groupByCalcs: _.values(dctMtrcSQLgroupByCalcs),
                order: this._createOptions.orderBy
            };
        }

        if (bCountOnly) {
            request.bCountOnly = true;
            delete request.groupByCalcs;
        }


        const results = await PASearch.findByRequest(request);

        // convert numbers from string to number
        _.forEach(results, (row: any) => {
            _.forEach(arrNumberSQLColumns, (colName) => {
                row[colName] = +row[colName];
            });
        });

        // Execute Output Calcs in calc order
        for (const coMetricId of outputCalcOrder) {
            const coMetric = self._dctMetricOutputCalc[coMetricId];

            for (const aRow of results) {
                const scope: any = {};
                _.forEach(coMetric.requiredMetricsIds, (reqMtrcId) => {
                    scope[reqMtrcId] = self.getRowMetric(aRow, reqMtrcId);
                });
                const calcValue = coMetric.evalFunc.evaluate(scope);
                self.setRowData(aRow, coMetric.storeKey, calcValue);
            }
        }

        // Raw procedure calls can't take a SQL "where" clause, so attribute-only filters (normally
        // applied above via "where: cnd") must also be applied here, alongside the metric/mix filters
        // every other provider already re-applies post-fetch.
        if (bIsRawProcCall) {
            const postFetchFilters = _.union(grpFlt.attributeOnly, grpFlt.metricOnly, grpFlt.mix);
            if (postFetchFilters.length) {
                return this.Rows_filter(results, postFetchFilters);
            }
            return results;
        }

        // Apply any metric/ mix filters
        if (grpFlt.metricOnly.length || grpFlt.metricOnly.length) {
            const fltResults = this.Rows_filter(results, _.union(grpFlt.metricOnly, grpFlt.mix));
            return fltResults;
        }

        return results;
    }

    async getDistinctValues(attributeId: string, filters?: IdsFilterSpec[]): Promise<any[]> {
        const colName = this.dctColumnName[attributeId];
        if (_.isEmpty(colName)) return [];

        const attr = this._dctAttribute[attributeId];

        if (this._createOptions.objectName.indexOf("CALL ") > -1) {
            // A "CALL " objectName runs as a raw stored procedure call (see getDataRows) and can't be
            // wrapped in "SELECT DISTINCT ... FROM (...)", so pull the result set the same way the main
            // data rows are loaded and dedupe the column client-side instead.
            const rows = await this.getDataRows([attributeId], [], filters || []);
            const keys = _.uniq(_.map(rows, (r) => this.getRowAttribute(r, attributeId)));
            if (attr?.Type == "number") return _.map(keys, (n) => +n);
            return keys;
        }

        const grpFlt = this.getGroupFilters(filters || []);

        const cnd = this.createSearchCondition(grpFlt.attributeOnly);
        const request: ISearchRequest = {
            model: this._createOptions.objectName,
            fields: this._searchSchema,
            limit: 0, // No Limit
            where: cnd,
            distinct: [colName]
        };

        const results = await PASearch.findByRequest(request);

        const keys = _.map(results, (r) => r[colName]);
        if (attr?.Type == "number") return _.map(keys, (n) => +n);
        return keys;
    }

    async getValueRange(attributeId: string, filters?: IdsFilterSpec[]): Promise<{ min: any; max: any }> {
        const colName = this.dctColumnName[attributeId];
        if (_.isEmpty(colName)) return { min: undefined, max: undefined };

        const grpFlt = this.getGroupFilters(filters || []);

        const cnd = this.createSearchCondition(grpFlt.attributeOnly || []);
        const request: ISearchRequest = {
            model: this._createOptions.objectName,
            fields: this._searchSchema,
            limit: 0, // No Limit
            where: cnd,
            distinct: [],
            groupByCalcs: [
                { func: "MIN", srcCalc: colName, outFieldName: "MIN_VALUE" },
                { func: "MAX", srcCalc: colName, outFieldName: "MAX_VALUE" }
            ]
        };

        const results = await PASearch.findByRequest(request);
        if (results.length) {
            const attr = this._dctAttribute[attributeId];
            const range = { min: results[0].MIN_VALUE, max: results[0].MAX_VALUE };
            if (attr?.Type == "number") {
                range.min = +range.min;
                range.max = +range.max;
            }
            return range;
        } else return { min: undefined, max: undefined };
    }
}
